import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import axios from 'axios';
import { setUserInfo } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || null;
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Login with credentials to receive httpOnly cookie
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`, 
        formData,
        { withCredentials: true }
      );
      
      // Store user info for UI (non-sensitive data only)
      // The auth token is now in an httpOnly cookie
      setUserInfo(response.data.user);
      
      // Redirect based on role or query param
      if (redirectTo) {
        navigate(redirectTo);
      } else if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <Card className="login-card">
          <CardHeader>
            <CardTitle>Welcome Back</CardTitle>
            <CardDescription>Log in to your GoCalFit account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="login-email"
                />
              </div>
              
              <div className="form-group">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="login-password"
                />
              </div>

              {error && (
                <div className="error-message" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                className="login-button"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Logging in...' : 'Log In'}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="login-footer">
            <p>Don't have an account? <Link to="/signup">Sign up</Link> | <Link to="/forgot-password" className="forgot-link">Forgot password?</Link></p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
